import { Fragment, ReactNode, useCallback, useEffect, useRef } from 'react';
import {
  Table as TanstackTable,
  Row,
  RowData,
  flexRender,
} from '@tanstack/react-table';
import { AnimatePresence, motion } from 'framer-motion';
import './Table.css';
import { Pagination, PaginationProps } from '../Pagination/Pagination';
import { TableHeader } from './TableHead';
import { cn } from '../../utils';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    cellClassName?: string;
    headerClassName?: string;
  }
}

type TableProps<T> = {
  table: TanstackTable<T>;
  isLoading?: boolean;
  pagination?: PaginationProps;
  renderSubComponent?: (props: { row: Row<T> }) => ReactNode;
  getRowClassName?: (row: Row<T>) => string | undefined;
};

const CellSkeleton = () => (
  <div className="lf:my-1 lf:h-6 lf:w-full lf:min-w-[20px] lf:animate-pulse lf:rounded-xl lf:bg-neutral-100" />
);

const TableRow = <T,>({
  row,
  isLoading,
  getRowClassName,
}: {
  row: Row<T>;
  isLoading?: boolean;
  getRowClassName?: (row: Row<T>) => string | undefined;
}) => (
  <tr role="row" className={getRowClassName?.(row)}>
    {row.getVisibleCells().map((cell) => (
      <td
        key={cell.id}
        className={cn(cell.column.columnDef.meta?.cellClassName)}
      >
        {isLoading ? (
          <CellSkeleton key={`cell-skeleton-${cell.id}`} />
        ) : (
          flexRender(cell.column.columnDef.cell, cell.getContext())
        )}
      </td>
    ))}
  </tr>
);

/**
 * Measures header cell positions and exposes them as CSS custom properties
 * on the table element, so sub-row content can align with columns.
 *
 * Sets: --col-{i}-left, --col-{i}-width for each header cell.
 */
const useColumnMeasure = (
  tableRef: React.RefObject<HTMLTableElement | null>,
  enabled: boolean,
) => {
  const measure = useCallback(() => {
    const table = tableRef.current;
    if (!table) return;
    const cells = table.querySelectorAll('thead th');
    cells.forEach((th, i) => {
      const el = th as HTMLElement;
      table.style.setProperty(`--col-${i}-left`, `${el.offsetLeft}px`);
      table.style.setProperty(`--col-${i}-width`, `${el.offsetWidth}px`);
    });
  }, [tableRef]);

  useEffect(() => {
    if (!enabled) return;
    measure();
    const observer = new ResizeObserver(measure);
    if (tableRef.current) observer.observe(tableRef.current);
    return () => observer.disconnect();
  }, [enabled, measure, tableRef]);
};

export const Table = <T,>({
  table,
  isLoading,
  pagination,
  renderSubComponent,
  getRowClassName,
}: TableProps<T>) => {
  const rows = table.getRowModel().rows;
  const tableRef = useRef<HTMLTableElement>(null);
  const isExpandable = !!renderSubComponent;

  useColumnMeasure(tableRef, isExpandable);

  return (
    <div className="lf:flex lf:flex-col lf:gap-5">
      <div className="l-table-container lf:overflow-auto">
        <table ref={tableRef} className="l-table lf:w-full">
          <TableHeader headerGroups={table.getHeaderGroups()} />
          <tbody className="l-table-body lf:w-full">
            {rows.map((row) => (
              <Fragment key={row.id}>
                <TableRow
                  row={row}
                  isLoading={isLoading}
                  getRowClassName={getRowClassName}
                />
                {renderSubComponent && (
                  <AnimatePresence initial={false}>
                    {row.getIsExpanded() && (
                      <tr
                        key={`${row.id}-sub`}
                        className="l-table-subrow"
                        role="region"
                        id={`row-${row.id}-panel`}
                        aria-labelledby={`row-${row.id}-expand-btn`}
                      >
                        <td
                          colSpan={row.getVisibleCells().length}
                          className="lf:!p-0"
                        >
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2, ease: 'easeInOut' }}
                            style={{ overflow: 'hidden' }}
                          >
                            {renderSubComponent({ row })}
                          </motion.div>
                        </td>
                      </tr>
                    )}
                  </AnimatePresence>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {pagination ? (
        <Pagination className="l-table-pagination" {...pagination} />
      ) : null}
    </div>
  );
};
